/**
 * PostFxPass — a single fragment-shader pass that chains ~10 stylization
 * effects, each gated by an independent strength uniform.
 *
 * Inspired by vizz.fm's post-processing stack (RGB shift, glitch, CRT,
 * kaleidoscope, pixelate, dither, dot screen, film grain, tilt-shift,
 * radial blur). Bundling them into one pass keeps the cost ~one extra
 * fullscreen draw regardless of how many are enabled, instead of N
 * separate passes — and uniform branching on modern GPUs costs less than
 * the per-pass framebuffer thrash.
 *
 * Effect order matters for visual correctness:
 *   1. Spatial distortions remap the sampled UV first (kaleidoscope,
 *      pixelate, swirl).
 *   2. Channel sampling (RGB shift) then samples the warped UV with
 *      per-channel offsets.
 *   3. Color stylization (sepia, dot-screen, dither) operates on the
 *      sampled colour.
 *   4. Framing overlays (CRT scanlines, film grain, tilt-shift, vignette,
 *      radial blur) apply last so they read as "on top of" the image.
 *
 * Each strength is 0..1 and 0 = exact passthrough (no perceptible cost
 * difference from disabled, since the math collapses to mix(x, x, 0) = x).
 */

import { ShaderMaterial, Uniform, Vector2 } from "three";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";

export interface PostFxParams {
  /** Master enable — when false, the pass is skipped entirely. */
  enabled: boolean;
  /** Per-channel chromatic-aberration offset. */
  rgbShift: number;
  /** Direction of the rgb shift (radians). */
  rgbShiftAngle: number;
  /** Per-effect opacity (final mix vs. previous-pass color). */
  rgbShiftOpacity: number;
  /** Periodic horizontal slice tear + colour fringing (beat-synced upstream). */
  glitch: number;
  glitchSpeed: number;
  glitchBlock: number;
  glitchOpacity: number;
  /** Curved scanlines + slight tint, CRT-style. */
  crt: number;
  crtCurvature: number;
  crtScanlineIntensity: number;
  crtRgbOffset: number;
  crtOpacity: number;
  /** Mirrored radial slices. */
  kaleidoscope: number;
  kaleidoscopeSegments: number;
  kaleidoscopeRotation: number;
  kaleidoscopeCenterX: number;
  kaleidoscopeCenterY: number;
  kaleidoscopeOpacity: number;
  /** Block-pixelation. */
  pixelate: number;
  pixelateOpacity: number;
  /** Ordered 4×4 Bayer dither. */
  dither: number;
  ditherLevels: number;
  ditherPixelScale: number;
  ditherOpacity: number;
  /** Halftone dot screen. */
  dotScreen: number;
  dotScreenAngle: number;
  dotScreenCenterX: number;
  dotScreenCenterY: number;
  dotScreenOpacity: number;
  /** Animated film-grain noise. */
  filmGrain: number;
  filmGrainScanlineIntensity: number;
  filmGrainScanlineCount: number;
  filmGrainGrayscale: number;
  filmGrainOpacity: number;
  /** Top/bottom defocus, leaves a sharp central band. */
  tiltShift: number;
  tiltShiftFocusPos: number;
  tiltShiftFocusWidth: number;
  tiltShiftFalloff: number;
  tiltShiftOpacity: number;
  /** Outward radial blur (zoom blur). */
  radialBlur: number;
  radialBlurCenterX: number;
  radialBlurCenterY: number;
  radialBlurInnerRadius: number;
  radialBlurFalloff: number;
  radialBlurOpacity: number;
  /** Vignette (darken corners). */
  vignette: number;
  vignetteSize: number;
  vignetteOpacity: number;
  /** Twirl / swirl distortion. */
  swirl: number;
  swirlAngle: number;
  swirlRadius: number;
  swirlFalloff: number;
  swirlCenterX: number;
  swirlCenterY: number;
  swirlOpacity: number;
  /** Lens-style barrel distortion. */
  barrel: number;
  barrelAmountX: number;
  barrelAmountY: number;
  barrelCenterX: number;
  barrelCenterY: number;
  barrelZoom: number;
  barrelChroma: number;
  barrelVignette: number;
  barrelVignetteSize: number;
  barrelOpacity: number;
  /** Tiles the screen into N×M copies. */
  gridTile: number;
  gridTileColumns: number;
  gridTileRows: number;
  gridTileGap: number;
  gridTileMirror: number;
  gridTileCenterX: number;
  gridTileCenterY: number;
  gridTileOpacity: number;
  /** Sepia. */
  sepia: number;
  sepiaOpacity: number;
  /** Bleach bypass. */
  bleach: number;
  bleachOpacity: number;
}

export const DEFAULT_POSTFX: PostFxParams = {
  enabled: true,
  rgbShift: 0, rgbShiftAngle: 0, rgbShiftOpacity: 1,
  glitch: 0, glitchSpeed: 1, glitchBlock: 1, glitchOpacity: 1,
  crt: 0, crtCurvature: 1, crtScanlineIntensity: 1, crtRgbOffset: 0, crtOpacity: 1,
  kaleidoscope: 0, kaleidoscopeSegments: 6, kaleidoscopeRotation: 0,
  kaleidoscopeCenterX: 0.5, kaleidoscopeCenterY: 0.5, kaleidoscopeOpacity: 1,
  pixelate: 0, pixelateOpacity: 1,
  dither: 0, ditherLevels: 6, ditherPixelScale: 1, ditherOpacity: 1,
  dotScreen: 0, dotScreenAngle: 0, dotScreenCenterX: 0.5, dotScreenCenterY: 0.5, dotScreenOpacity: 1,
  filmGrain: 0, filmGrainScanlineIntensity: 0, filmGrainScanlineCount: 800, filmGrainGrayscale: 0, filmGrainOpacity: 1,
  tiltShift: 0, tiltShiftFocusPos: 0.5, tiltShiftFocusWidth: 0.25, tiltShiftFalloff: 1, tiltShiftOpacity: 1,
  radialBlur: 0, radialBlurCenterX: 0.5, radialBlurCenterY: 0.5, radialBlurInnerRadius: 0, radialBlurFalloff: 1, radialBlurOpacity: 1,
  vignette: 0, vignetteSize: 1, vignetteOpacity: 1,
  swirl: 0, swirlAngle: 1, swirlRadius: 0.5, swirlFalloff: 1, swirlCenterX: 0.5, swirlCenterY: 0.5, swirlOpacity: 1,
  barrel: 0, barrelAmountX: 1, barrelAmountY: 1, barrelCenterX: 0.5, barrelCenterY: 0.5,
  barrelZoom: 1, barrelChroma: 0, barrelVignette: 0, barrelVignetteSize: 1, barrelOpacity: 1,
  gridTile: 0, gridTileColumns: 2, gridTileRows: 0, gridTileGap: 0, gridTileMirror: 0,
  gridTileCenterX: 0.5, gridTileCenterY: 0.5, gridTileOpacity: 1,
  sepia: 0, sepiaOpacity: 1,
  bleach: 0, bleachOpacity: 1,
};

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  uniform sampler2D tDiffuse;
  uniform vec2 uResolution;
  uniform float uTime;
  uniform float uBeat;

  uniform float uRgbShift; uniform float uRgbShiftAngle; uniform float uRgbShiftOpacity;
  uniform float uGlitch; uniform float uGlitchSpeed; uniform float uGlitchBlock; uniform float uGlitchOpacity;
  uniform float uCrt; uniform float uCrtCurvature; uniform float uCrtScanIntensity; uniform float uCrtRgbOffset; uniform float uCrtOpacity;
  uniform float uKaleidoscope; uniform float uKaleidoscopeSegments; uniform float uKaleidoscopeRotation;
  uniform vec2  uKaleidoscopeCenter; uniform float uKaleidoscopeOpacity;
  uniform float uPixelate; uniform float uPixelateOpacity;
  uniform float uDither; uniform float uDitherLevels; uniform float uDitherPixelScale; uniform float uDitherOpacity;
  uniform float uDotScreen; uniform float uDotScreenAngle; uniform vec2 uDotScreenCenter; uniform float uDotScreenOpacity;
  uniform float uFilmGrain; uniform float uFilmScanIntensity; uniform float uFilmScanCount; uniform float uFilmGrayscale; uniform float uFilmOpacity;
  uniform float uTiltShift; uniform float uTiltFocusPos; uniform float uTiltFocusWidth; uniform float uTiltFalloff; uniform float uTiltOpacity;
  uniform float uRadialBlur; uniform vec2 uRadialBlurCenter; uniform float uRadialBlurInner; uniform float uRadialBlurFalloff; uniform float uRadialBlurOpacity;
  uniform float uVignette; uniform float uVignetteSize; uniform float uVignetteOpacity;
  uniform float uSwirl; uniform float uSwirlAngle; uniform float uSwirlRadius; uniform float uSwirlFalloff; uniform vec2 uSwirlCenter; uniform float uSwirlOpacity;
  uniform float uBarrel; uniform float uBarrelAmountX; uniform float uBarrelAmountY;
  uniform vec2  uBarrelCenter; uniform float uBarrelZoom; uniform float uBarrelChroma;
  uniform float uBarrelVignette; uniform float uBarrelVignetteSize; uniform float uBarrelOpacity;
  uniform float uGridTile; uniform float uGridTileColumns; uniform float uGridTileRows; uniform float uGridTileGap;
  uniform float uGridTileMirror; uniform vec2 uGridTileCenter; uniform float uGridTileOpacity;
  uniform float uSepia; uniform float uSepiaOpacity;
  uniform float uBleach; uniform float uBleachOpacity;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float bayer4(vec2 p) {
    int x = int(mod(p.x, 4.0));
    int y = int(mod(p.y, 4.0));
    int idx = x + y * 4;
    float m = 0.0;
         if (idx == 0)  m =  0.0/16.0;
    else if (idx == 1)  m =  8.0/16.0;
    else if (idx == 2)  m =  2.0/16.0;
    else if (idx == 3)  m = 10.0/16.0;
    else if (idx == 4)  m = 12.0/16.0;
    else if (idx == 5)  m =  4.0/16.0;
    else if (idx == 6)  m = 14.0/16.0;
    else if (idx == 7)  m =  6.0/16.0;
    else if (idx == 8)  m =  3.0/16.0;
    else if (idx == 9)  m = 11.0/16.0;
    else if (idx == 10) m =  1.0/16.0;
    else if (idx == 11) m =  9.0/16.0;
    else if (idx == 12) m = 15.0/16.0;
    else if (idx == 13) m =  7.0/16.0;
    else if (idx == 14) m = 13.0/16.0;
    else if (idx == 15) m =  5.0/16.0;
    return m - 0.5;
  }

  void main() {
    vec2 uv = vUv;
    float aspect = uResolution.x / uResolution.y;

    // ---- 0. GridTile (vizz: tile screen into N×M with gap + mirror). ----
    if (uGridTile > 0.001) {
      float cols = max(1.0, floor(uGridTileColumns));
      float rows = uGridTileRows >= 1.0 ? floor(uGridTileRows) : max(1.0, floor(cols / max(aspect, 0.5)));
      vec2 c2 = (uv - uGridTileCenter) + vec2(0.5);
      vec2 tiled = vec2(fract(c2.x * cols), fract(c2.y * rows));
      // Mirror Adjacent: flip every other tile.
      if (uGridTileMirror > 0.5) {
        float cellX = floor(c2.x * cols); float cellY = floor(c2.y * rows);
        if (mod(cellX, 2.0) >= 1.0) tiled.x = 1.0 - tiled.x;
        if (mod(cellY, 2.0) >= 1.0) tiled.y = 1.0 - tiled.y;
      }
      vec2 mixed = mix(uv, tiled, clamp(uGridTile, 0.0, 1.0) * uGridTileOpacity);
      // Optional gap: darken near tile edges.
      if (uGridTileGap > 0.001) {
        vec2 gd = abs(tiled - 0.5);
        float edge = max(gd.x, gd.y);
        float gap = smoothstep(0.5 - uGridTileGap * 0.05, 0.5, edge);
        mixed = mix(mixed, vec2(0.5), gap * uGridTileOpacity);
      }
      uv = mixed;
    }

    // ---- 0b. Barrel / pincushion (vizz-parity: per-axis amount, center, zoom). ----
    if (abs(uBarrel) > 0.001) {
      // Aspect-correct centred coords so distortion is rotation-symmetric.
      vec2 dc = (uv - uBarrelCenter);
      dc.x *= aspect;
      // Per-axis weight on the radial term (vizz amountX/amountY).
      float r2 = dc.x * dc.x * uBarrelAmountX + dc.y * dc.y * uBarrelAmountY;
      // Gentle coefficient (0.5) keeps amount=-1 visibly distorted without inversion.
      // Clamp so we never flip past 0 (which would send corners to opposite side).
      float k = max(-0.95, uBarrel * r2 * 0.5);
      vec2 distorted = dc * (1.0 + k);
      // Apply zoom (vizz: <1 zooms in, >1 zooms out — match by dividing).
      distorted /= max(0.05, uBarrelZoom);
      // Back to UV space.
      distorted.x /= aspect;
      vec2 outUv = uBarrelCenter + distorted;
      uv = mix(uv, outUv, uBarrelOpacity);
    }

    // ---- 1. Spatial distortions on UV ----

    // Kaleidoscope — fold polar angle into N slices and mirror, around custom center.
    if (uKaleidoscope > 0.001) {
      vec2 ck = uv - uKaleidoscopeCenter;
      ck.x *= aspect;
      float r = length(ck);
      float a = atan(ck.y, ck.x) + uKaleidoscopeRotation;
      float segs = max(2.0, floor(uKaleidoscopeSegments));
      float seg = 6.2831853 / segs;
      a = mod(a, seg);
      a = abs(a - seg * 0.5);
      vec2 kuv = vec2(cos(a), sin(a)) * r;
      kuv.x /= aspect;
      kuv += uKaleidoscopeCenter;
      uv = mix(uv, kuv, clamp(uKaleidoscope, 0.0, 1.0) * uKaleidoscopeOpacity);
    }

    // Swirl — rotate UV by angle that depends on radius, custom center.
    if (uSwirl > 0.001) {
      vec2 sc = uv - uSwirlCenter;
      float r = length(sc);
      float radius = max(0.05, uSwirlRadius);
      float falloff = max(0.05, uSwirlFalloff);
      float wt = 1.0 - smoothstep(0.0, radius, r);
      wt = pow(wt, falloff);
      float ang = uSwirl * uSwirlAngle * 2.5 * wt;
      float cs = cos(ang), sn = sin(ang);
      sc = mat2(cs, -sn, sn, cs) * sc;
      uv = mix(uv, sc + uSwirlCenter, uSwirlOpacity);
    }

    // Pixelate.
    if (uPixelate > 0.001) {
      float blocks = mix(uResolution.y, 60.0, clamp(uPixelate, 0.0, 1.0));
      vec2 cell = vec2(blocks * uResolution.x / uResolution.y, blocks);
      vec2 pu = (floor(uv * cell) + 0.5) / cell;
      uv = mix(uv, pu, uPixelateOpacity);
    }

    // Glitch.
    if (uGlitch > 0.001) {
      float strip = floor(uv.y * 80.0 / max(0.1, uGlitchBlock));
      float r1 = hash(vec2(strip, floor(uTime * 12.0 * uGlitchSpeed)));
      float burst = step(0.95 - 0.15 * uGlitch, r1);
      uv.x += burst * (r1 - 0.5) * 0.15 * uGlitch * uGlitchOpacity;
    }

    // ---- Sample ----
    vec3 col;

    // Radial blur (with center, inner radius, falloff).
    if (uRadialBlur > 0.001) {
      vec2 dir = uv - uRadialBlurCenter;
      float r = length(dir);
      float wt = smoothstep(uRadialBlurInner, uRadialBlurInner + uRadialBlurFalloff, r);
      vec3 acc = vec3(0.0);
      const int N = 8;
      for (int i = 0; i < N; i++) {
        float t = float(i) / float(N - 1);
        float scale = 1.0 - uRadialBlur * 0.12 * t * wt;
        vec2 suv = uRadialBlurCenter + dir * scale;
        acc += texture2D(tDiffuse, suv).rgb;
      }
      vec3 base = texture2D(tDiffuse, uv).rgb;
      col = mix(base, acc / float(N), uRadialBlurOpacity);
    } else {
      col = texture2D(tDiffuse, uv).rgb;
    }

    // Barrel chroma offset (per-channel).
    if (abs(uBarrel) > 0.001 && uBarrelChroma > 0.001) {
      vec2 dc = uv - uBarrelCenter;
      vec2 dir = normalize(dc + 1e-5);
      float amt = 0.012 * uBarrelChroma;
      col.r = texture2D(tDiffuse, uv + dir * amt).r;
      col.b = texture2D(tDiffuse, uv - dir * amt).b;
    }

    // RGB shift (chromatic aberration with arbitrary direction).
    if (uRgbShift > 0.001) {
      vec2 dir = vec2(cos(uRgbShiftAngle), sin(uRgbShiftAngle));
      float amt = 0.012 * uRgbShift;
      vec3 shifted = col;
      shifted.r = texture2D(tDiffuse, uv + dir * amt).r;
      shifted.b = texture2D(tDiffuse, uv - dir * amt).b;
      col = mix(col, shifted, uRgbShiftOpacity);
    }

    // ---- 3. Color stylization ----

    if (uDotScreen > 0.001) {
      float scale = mix(2.0, 0.6, clamp(uDotScreen, 0.0, 1.0));
      float ang = uDotScreenAngle;
      vec2 dc = uv - uDotScreenCenter;
      mat2 rot = mat2(cos(ang), -sin(ang), sin(ang), cos(ang));
      vec2 q = (rot * dc) * uResolution / scale;
      vec2 g = fract(q) - 0.5;
      float d = length(g);
      float lum = dot(col, vec3(0.299, 0.587, 0.114));
      float dot_ = step(d, 0.5 - 0.5 * lum);
      col = mix(col, col * (0.4 + 0.6 * dot_), uDotScreen * uDotScreenOpacity);
    }

    if (uDither > 0.001) {
      float steps = max(2.0, floor(uDitherLevels));
      vec2 px = uv * uResolution / max(0.5, uDitherPixelScale);
      float m = bayer4(px);
      vec3 dith = floor(col * steps + 0.5 + m * uDither) / steps;
      col = mix(col, dith, uDitherOpacity);
    }

    if (uSepia > 0.001) {
      vec3 s = vec3(
        dot(col, vec3(0.393, 0.769, 0.189)),
        dot(col, vec3(0.349, 0.686, 0.168)),
        dot(col, vec3(0.272, 0.534, 0.131))
      );
      col = mix(col, s, uSepia * uSepiaOpacity);
    }
    if (uBleach > 0.001) {
      float lum = dot(col, vec3(0.299, 0.587, 0.114));
      vec3 bb = col * (1.0 - lum) + vec3(lum) * lum * 1.5;
      col = mix(col, bb, uBleach * uBleachOpacity);
    }

    // ---- 4. Framing overlays ----

    // CRT.
    if (uCrt > 0.001) {
      float scan = 0.5 + 0.5 * sin(uv.y * uResolution.y * 1.4 * max(0.1, uCrtScanIntensity));
      float scanMask = mix(1.0, 0.65 + 0.35 * scan, uCrt * uCrtOpacity);
      col *= scanMask;
      vec2 cc = (uv - 0.5) * (1.05 + uCrtCurvature * 0.2);
      float curve = 1.0 - dot(cc, cc) * 0.55 * uCrt;
      col *= max(0.6, curve);
      // RGB offset.
      if (uCrtRgbOffset > 0.001) {
        float a = uCrtRgbOffset * 0.005;
        col.r = mix(col.r, texture2D(tDiffuse, uv + vec2(a, 0.0)).r, uCrtOpacity);
        col.b = mix(col.b, texture2D(tDiffuse, uv - vec2(a, 0.0)).b, uCrtOpacity);
      }
    }

    // Tilt-shift.
    if (uTiltShift > 0.001) {
      float dy = abs(uv.y - uTiltFocusPos);
      float band = 1.0 - smoothstep(uTiltFocusWidth * 0.5, uTiltFocusWidth * 0.5 + uTiltFalloff * 0.4, dy);
      vec2 px = 1.0 / uResolution;
      vec3 blur = (
        texture2D(tDiffuse, uv + vec2( 1.5, 0.0) * px).rgb +
        texture2D(tDiffuse, uv + vec2(-1.5, 0.0) * px).rgb +
        texture2D(tDiffuse, uv + vec2(0.0,  1.5) * px).rgb +
        texture2D(tDiffuse, uv + vec2(0.0, -1.5) * px).rgb
      ) * 0.25;
      col = mix(blur, col, mix(1.0, band, uTiltShift * uTiltOpacity));
    }

    // Vignette (independent + barrel-coupled).
    if (uVignette > 0.001) {
      vec2 vc = uv - 0.5;
      float v = 1.0 - smoothstep(0.25 * uVignetteSize, 0.75 * uVignetteSize, length(vc) * 1.4);
      col *= mix(1.0, v, uVignette * uVignetteOpacity);
    }
    if (uBarrelVignette > 0.001) {
      vec2 vc = uv - uBarrelCenter;
      float v = 1.0 - smoothstep(0.25 * uBarrelVignetteSize, 0.75 * uBarrelVignetteSize, length(vc) * 1.4);
      col *= mix(1.0, v, uBarrelVignette * uBarrelOpacity);
    }

    // Film grain + scanlines + grayscale.
    if (uFilmGrain > 0.001) {
      float n = hash(uv * uResolution + uTime * 60.0) - 0.5;
      col += n * 0.18 * uFilmGrain * uFilmOpacity;
      if (uFilmScanIntensity > 0.001) {
        float s = 0.5 + 0.5 * sin(uv.y * max(20.0, uFilmScanCount));
        col *= mix(1.0, s, uFilmScanIntensity * uFilmOpacity);
      }
      if (uFilmGrayscale > 0.001) {
        float lum = dot(col, vec3(0.299, 0.587, 0.114));
        col = mix(col, vec3(lum), uFilmGrayscale * uFilmOpacity);
      }
    }

    gl_FragColor = vec4(col, 1.0);
  }
`;

/**
 * Three.js ShaderPass wrapper. Mutate `params` directly each frame; the
 * renderer copies them into the uniforms before composer.render().
 */
export class PostFxPass extends ShaderPass {
  public params: PostFxParams = { ...DEFAULT_POSTFX };

  constructor(width: number, height: number) {
    const u = (v: unknown): Uniform => new Uniform(v);
    const material = new ShaderMaterial({
      uniforms: {
        tDiffuse: u(null),
        uResolution: u(new Vector2(width, height)),
        uTime: u(0), uBeat: u(0),
        uRgbShift: u(0), uRgbShiftAngle: u(0), uRgbShiftOpacity: u(1),
        uGlitch: u(0), uGlitchSpeed: u(1), uGlitchBlock: u(1), uGlitchOpacity: u(1),
        uCrt: u(0), uCrtCurvature: u(1), uCrtScanIntensity: u(1), uCrtRgbOffset: u(0), uCrtOpacity: u(1),
        uKaleidoscope: u(0), uKaleidoscopeSegments: u(6), uKaleidoscopeRotation: u(0),
        uKaleidoscopeCenter: u(new Vector2(0.5, 0.5)), uKaleidoscopeOpacity: u(1),
        uPixelate: u(0), uPixelateOpacity: u(1),
        uDither: u(0), uDitherLevels: u(6), uDitherPixelScale: u(1), uDitherOpacity: u(1),
        uDotScreen: u(0), uDotScreenAngle: u(0),
        uDotScreenCenter: u(new Vector2(0.5, 0.5)), uDotScreenOpacity: u(1),
        uFilmGrain: u(0), uFilmScanIntensity: u(0), uFilmScanCount: u(800), uFilmGrayscale: u(0), uFilmOpacity: u(1),
        uTiltShift: u(0), uTiltFocusPos: u(0.5), uTiltFocusWidth: u(0.25), uTiltFalloff: u(1), uTiltOpacity: u(1),
        uRadialBlur: u(0), uRadialBlurCenter: u(new Vector2(0.5, 0.5)),
        uRadialBlurInner: u(0), uRadialBlurFalloff: u(1), uRadialBlurOpacity: u(1),
        uVignette: u(0), uVignetteSize: u(1), uVignetteOpacity: u(1),
        uSwirl: u(0), uSwirlAngle: u(1), uSwirlRadius: u(0.5), uSwirlFalloff: u(1),
        uSwirlCenter: u(new Vector2(0.5, 0.5)), uSwirlOpacity: u(1),
        uBarrel: u(0), uBarrelAmountX: u(1), uBarrelAmountY: u(1),
        uBarrelCenter: u(new Vector2(0.5, 0.5)), uBarrelZoom: u(1), uBarrelChroma: u(0),
        uBarrelVignette: u(0), uBarrelVignetteSize: u(1), uBarrelOpacity: u(1),
        uGridTile: u(0), uGridTileColumns: u(2), uGridTileRows: u(0), uGridTileGap: u(0),
        uGridTileMirror: u(0), uGridTileCenter: u(new Vector2(0.5, 0.5)), uGridTileOpacity: u(1),
        uSepia: u(0), uSepiaOpacity: u(1),
        uBleach: u(0), uBleachOpacity: u(1),
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
    });
    super(material, "tDiffuse");
  }

  setSize(width: number, height: number): void {
    super.setSize(width, height);
    const res = this.material.uniforms.uResolution.value as Vector2;
    res.set(width, height);
  }

  syncUniforms(timeSec: number, beat: number): void {
    const u = this.material.uniforms;
    const p = this.params;
    u.uTime.value = timeSec; u.uBeat.value = beat;
    u.uRgbShift.value = p.rgbShift; u.uRgbShiftAngle.value = p.rgbShiftAngle; u.uRgbShiftOpacity.value = p.rgbShiftOpacity;
    u.uGlitch.value = p.glitch; u.uGlitchSpeed.value = p.glitchSpeed; u.uGlitchBlock.value = p.glitchBlock; u.uGlitchOpacity.value = p.glitchOpacity;
    u.uCrt.value = p.crt; u.uCrtCurvature.value = p.crtCurvature;
    u.uCrtScanIntensity.value = p.crtScanlineIntensity; u.uCrtRgbOffset.value = p.crtRgbOffset; u.uCrtOpacity.value = p.crtOpacity;
    u.uKaleidoscope.value = p.kaleidoscope; u.uKaleidoscopeSegments.value = p.kaleidoscopeSegments;
    u.uKaleidoscopeRotation.value = p.kaleidoscopeRotation;
    (u.uKaleidoscopeCenter.value as Vector2).set(p.kaleidoscopeCenterX, p.kaleidoscopeCenterY);
    u.uKaleidoscopeOpacity.value = p.kaleidoscopeOpacity;
    u.uPixelate.value = p.pixelate; u.uPixelateOpacity.value = p.pixelateOpacity;
    u.uDither.value = p.dither; u.uDitherLevels.value = p.ditherLevels;
    u.uDitherPixelScale.value = p.ditherPixelScale; u.uDitherOpacity.value = p.ditherOpacity;
    u.uDotScreen.value = p.dotScreen; u.uDotScreenAngle.value = p.dotScreenAngle;
    (u.uDotScreenCenter.value as Vector2).set(p.dotScreenCenterX, p.dotScreenCenterY);
    u.uDotScreenOpacity.value = p.dotScreenOpacity;
    u.uFilmGrain.value = p.filmGrain; u.uFilmScanIntensity.value = p.filmGrainScanlineIntensity;
    u.uFilmScanCount.value = p.filmGrainScanlineCount; u.uFilmGrayscale.value = p.filmGrainGrayscale;
    u.uFilmOpacity.value = p.filmGrainOpacity;
    u.uTiltShift.value = p.tiltShift; u.uTiltFocusPos.value = p.tiltShiftFocusPos;
    u.uTiltFocusWidth.value = p.tiltShiftFocusWidth; u.uTiltFalloff.value = p.tiltShiftFalloff;
    u.uTiltOpacity.value = p.tiltShiftOpacity;
    u.uRadialBlur.value = p.radialBlur;
    (u.uRadialBlurCenter.value as Vector2).set(p.radialBlurCenterX, p.radialBlurCenterY);
    u.uRadialBlurInner.value = p.radialBlurInnerRadius; u.uRadialBlurFalloff.value = p.radialBlurFalloff;
    u.uRadialBlurOpacity.value = p.radialBlurOpacity;
    u.uVignette.value = p.vignette; u.uVignetteSize.value = p.vignetteSize; u.uVignetteOpacity.value = p.vignetteOpacity;
    u.uSwirl.value = p.swirl; u.uSwirlAngle.value = p.swirlAngle;
    u.uSwirlRadius.value = p.swirlRadius; u.uSwirlFalloff.value = p.swirlFalloff;
    (u.uSwirlCenter.value as Vector2).set(p.swirlCenterX, p.swirlCenterY);
    u.uSwirlOpacity.value = p.swirlOpacity;
    u.uBarrel.value = p.barrel; u.uBarrelAmountX.value = p.barrelAmountX; u.uBarrelAmountY.value = p.barrelAmountY;
    (u.uBarrelCenter.value as Vector2).set(p.barrelCenterX, p.barrelCenterY);
    u.uBarrelZoom.value = p.barrelZoom; u.uBarrelChroma.value = p.barrelChroma;
    u.uBarrelVignette.value = p.barrelVignette; u.uBarrelVignetteSize.value = p.barrelVignetteSize;
    u.uBarrelOpacity.value = p.barrelOpacity;
    u.uGridTile.value = p.gridTile; u.uGridTileColumns.value = p.gridTileColumns;
    u.uGridTileRows.value = p.gridTileRows; u.uGridTileGap.value = p.gridTileGap;
    u.uGridTileMirror.value = p.gridTileMirror;
    (u.uGridTileCenter.value as Vector2).set(p.gridTileCenterX, p.gridTileCenterY);
    u.uGridTileOpacity.value = p.gridTileOpacity;
    u.uSepia.value = p.sepia; u.uSepiaOpacity.value = p.sepiaOpacity;
    u.uBleach.value = p.bleach; u.uBleachOpacity.value = p.bleachOpacity;
  }

  isAnyEffectActive(): boolean {
    if (!this.params.enabled) return false;
    const p = this.params;
    return (
      p.rgbShift > 0.001 || p.glitch > 0.001 || p.crt > 0.001 ||
      p.kaleidoscope > 0.001 || p.pixelate > 0.001 || p.dither > 0.001 ||
      p.dotScreen > 0.001 || p.filmGrain > 0.001 || p.tiltShift > 0.001 ||
      p.radialBlur > 0.001 || p.vignette > 0.001 || p.swirl > 0.001 ||
      Math.abs(p.barrel) > 0.001 || p.gridTile > 0.001 ||
      p.sepia > 0.001 || p.bleach > 0.001
    );
  }
}
